import { Compartment } from '@codemirror/state'
import type { EditorView } from '@codemirror/view'
import { type RefObject, useCallback, useEffect, useRef } from 'react'

import type { ParsedTemplateVariable } from './template-variable.interface'
import { mountEditor } from './template-variable-editor-mount.helper'
import { parseTemplateVariables } from './template-variable-parser.util'
import { useEditorValueSync } from './use-template-variable-editor-sync.hook'

interface UseTemplateVariableEditorParams {
  containerRef: RefObject<HTMLDivElement | null>
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  onVariablesChange?: (variableList: ParsedTemplateVariable[]) => void
  placeholder?: string
  isDisabled?: boolean
  minHeight?: string
}

/**
 * Hook that creates and manages a CodeMirror 6 EditorView for template variable editing.
 */
export const useTemplateVariableEditor = ({
  containerRef,
  value,
  onChange,
  onBlur,
  onVariablesChange,
  placeholder = '',
  isDisabled = false,
  minHeight = '200px',
}: UseTemplateVariableEditorParams): void => {
  const viewRef = useRef<EditorView | null>(null)
  const editableCompartmentRef = useRef(new Compartment())
  const onChangeRef = useRef(onChange)
  const onBlurRef = useRef(onBlur)
  const onVariablesChangeRef = useRef(onVariablesChange)
  const lastExternalValueRef = useRef(value)

  onChangeRef.current = onChange
  onBlurRef.current = onBlur
  onVariablesChangeRef.current = onVariablesChange

  const handleDocChange = useCallback((newDoc: string) => {
    onChangeRef.current(newDoc)
    onVariablesChangeRef.current?.(parseTemplateVariables(newDoc))
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    return mountEditor({
      container,
      value,
      placeholder,
      minHeight,
      isDisabled,
      editableCompartment: editableCompartmentRef.current,
      viewRef,
      lastExternalValueRef,
      onDocChange: handleDocChange,
      onBlur: () => onBlurRef.current?.(),
      onVariablesChange: onVariablesChangeRef.current,
    })
  }, [containerRef, placeholder, minHeight, handleDocChange, isDisabled])

  useEditorValueSync(viewRef, value, lastExternalValueRef)
}
